import { useCallback, useMemo, useState } from 'react';
import type { CccDashboardFilters, CccDashboardWorkspace, CccTimeFilter } from './types';

const todayKey = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const daysAgoKey = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const initialFilters: CccDashboardFilters = {
  timeFilter: 'today',
  customDateRange: [todayKey(), todayKey()],
  selectedWorkspaces: [],
  selectedUsers: [],
  selectedStatuses: [],
  requestsDateRange: [daysAgoKey(6), todayKey()],
};

const computeRange = (filter: CccTimeFilter): [string, string] | null => {
  const now = new Date();
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  switch (filter) {
    case 'today': {
      const k = fmt(now);
      return [k, k];
    }
    case 'week': {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return [fmt(start), fmt(end)];
    }
    case 'month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return [fmt(start), fmt(end)];
    }
    case 'quarter': {
      const start = new Date(now);
      start.setMonth(start.getMonth() - 3);
      return [fmt(start), fmt(now)];
    }
    case 'year': {
      const start = new Date(now);
      start.setFullYear(start.getFullYear() - 1);
      return [fmt(start), fmt(now)];
    }
    default:
      return null;
  }
};

export const useCccDashboardFilters = () => {
  const [filters, setFilters] = useState<CccDashboardFilters>(initialFilters);

  const setTimeFilter = useCallback((value: CccTimeFilter) => {
    setFilters((prev) => {
      if (value === 'custom') return { ...prev, timeFilter: 'custom' };
      const range = computeRange(value);
      return {
        ...prev,
        timeFilter: value,
        customDateRange: range ?? prev.customDateRange,
      };
    });
  }, []);

  const setCustomDateRange = useCallback((range: [string | null, string | null]) => {
    setFilters((prev) => ({
      ...prev,
      customDateRange: range,
      timeFilter: range[0] && range[1] ? 'custom' : prev.timeFilter,
    }));
  }, []);

  const setRequestsDateRange = useCallback((range: [string | null, string | null]) => {
    setFilters((prev) => ({ ...prev, requestsDateRange: range }));
  }, []);

  const setSelectedWorkspaces = useCallback((value: CccDashboardWorkspace[]) => {
    setFilters((prev) => ({ ...prev, selectedWorkspaces: value }));
  }, []);

  const setSelectedUsers = useCallback((value: number[]) => {
    setFilters((prev) => ({ ...prev, selectedUsers: value }));
  }, []);

  const setSelectedStatuses = useCallback((value: string[]) => {
    setFilters((prev) => ({ ...prev, selectedStatuses: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      ...initialFilters,
      customDateRange: [todayKey(), todayKey()],
      requestsDateRange: [daysAgoKey(6), todayKey()],
    });
  }, []);

  const effectiveDateRange = useMemo<[string, string]>(() => {
    if (filters.customDateRange[0] && filters.customDateRange[1]) {
      return [filters.customDateRange[0], filters.customDateRange[1]];
    }
    if (filters.timeFilter === 'all') return ['', ''];
    const computed = computeRange(filters.timeFilter);
    return computed ?? [todayKey(), todayKey()];
  }, [filters.customDateRange, filters.timeFilter]);

  const effectiveRequestsDateRange = useMemo<[string, string]>(() => {
    if (filters.requestsDateRange[0] && filters.requestsDateRange[1]) {
      return [filters.requestsDateRange[0], filters.requestsDateRange[1]];
    }
    return [daysAgoKey(6), todayKey()];
  }, [filters.requestsDateRange]);

  return {
    filters,
    effectiveDateRange,
    effectiveRequestsDateRange,
    setTimeFilter,
    setCustomDateRange,
    setRequestsDateRange,
    setSelectedWorkspaces,
    setSelectedUsers,
    setSelectedStatuses,
    resetFilters,
  };
};
